import type {
	MoveCallMetric,
	MoveCallMetrics,
	ProgrammableTransaction,
	SuiTransactionBlockResponse,
} from '@mysten/sui.js/client';

type MoveCallRef = {
	package: string;
	module: string;
	function: string;
};

const MS_3D = 3 * 24 * 60 * 60 * 1000;
const MS_7D = 7 * 24 * 60 * 60 * 1000;
const MS_30D = 30 * 24 * 60 * 60 * 1000;

function moveCallKey(ref: MoveCallRef): string {
	return `${ref.package}::${ref.module}::${ref.function}`;
}

export function extractMoveCallsFromTxBlock(tx: SuiTransactionBlockResponse): {
	calls: MoveCallRef[];
	ts: number;
} {
	const txn = tx.transaction?.data?.transaction;
	if (!txn || txn.kind !== 'ProgrammableTransaction') {
		return { calls: [], ts: Number(tx.timestampMs ?? 0) };
	}
	const pt = txn as ProgrammableTransaction & { kind: 'ProgrammableTransaction' };
	const ts = Number(tx.timestampMs ?? 0);
	const calls: MoveCallRef[] = [];
	for (const cmd of pt.transactions) {
		if ('MoveCall' in cmd) {
			const mc = cmd.MoveCall;
			calls.push({
				package: mc.package,
				module: mc.module,
				function: mc.function,
			});
		}
	}
	return { calls, ts };
}

function rankForWindow(
	entries: Map<string, { ref: MoveCallRef; count: number }>,
	limit: number,
): MoveCallMetric[] {
	return [...entries.values()]
		.sort((a, b) => b.count - a.count || moveCallKey(a.ref).localeCompare(moveCallKey(b.ref)))
		.slice(0, limit)
		.map((e) => [
			{ package: e.ref.package, module: e.ref.module, function: e.ref.function },
			String(e.count),
		]);
}

/** Aggregate move-call counts from scanned transactions (LOCAL fullnode has no indexer metrics). */
export function buildMoveCallMetricsFromTransactions(
	txs: SuiTransactionBlockResponse[],
	limit = 20,
): MoveCallMetrics {
	const now = Date.now();
	const buckets = {
		rank3Days: new Map<string, { ref: MoveCallRef; count: number }>(),
		rank7Days: new Map<string, { ref: MoveCallRef; count: number }>(),
		rank30Days: new Map<string, { ref: MoveCallRef; count: number }>(),
	};

	for (const tx of txs) {
		const { calls, ts } = extractMoveCallsFromTxBlock(tx);
		if (!calls.length || !ts) continue;
		const age = now - ts;
		const targets: (keyof typeof buckets)[] = [];
		if (age <= MS_30D) targets.push('rank30Days');
		if (age <= MS_7D) targets.push('rank7Days');
		if (age <= MS_3D) targets.push('rank3Days');

		for (const call of calls) {
			const key = moveCallKey(call);
			for (const window of targets) {
				const map = buckets[window];
				const cur = map.get(key);
				if (cur) cur.count += 1;
				else map.set(key, { ref: call, count: 1 });
			}
		}
	}

	return {
		rank3Days: rankForWindow(buckets.rank3Days, limit),
		rank7Days: rankForWindow(buckets.rank7Days, limit),
		rank30Days: rankForWindow(buckets.rank30Days, limit),
	};
}

export function mergeMoveCallMetrics(
	primary: MoveCallMetric[],
	seeds: MoveCallRef[],
	limit: number,
): MoveCallMetric[] {
	const seen = new Set(primary.map(([item]) => moveCallKey(item)));
	const merged: MoveCallMetric[] = [...primary];
	for (const ref of seeds) {
		const key = moveCallKey(ref);
		if (seen.has(key)) continue;
		seen.add(key);
		merged.push([{ package: ref.package, module: ref.module, function: ref.function }, '0']);
		if (merged.length >= limit) break;
	}
	return merged;
}

export function hasMoveCallMetrics(metrics: MoveCallMetrics | undefined): boolean {
	if (!metrics) return false;
	return (
		metrics.rank3Days.length > 0 ||
		metrics.rank7Days.length > 0 ||
		metrics.rank30Days.length > 0
	);
}
